import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ResponsiveContainer, ComposedChart, Area, Line, Bar, BarChart, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, ReferenceLine, LabelList
} from 'recharts';
import {
  fetchMachineCycle, fetchMachineTemperature, fetchMachineDrive, fetchMachineOverview, fetchMachineWatch1,
  fetchMachineSettingChanges
} from '../../../services/api';
import { useAutoRefresh } from '../../../hooks/useAutoRefresh';
import { useTranslation } from '../../../context/LanguageContext';
import './ProcessNew.css';

/* ===== 공정 탭 (Figma「사출기 대시보드 > 공정」새 디자인) =====
 * 서브 탭: 사이클 / 온도 / 구동 / 감시 / 설정값 을 모두 이 파일에서 구현한다.
 * 기존 Cycle·온도·구동부 탭은 그대로 두고 병행한다. 스타일은 pr- 접두어만 쓴다(Machine.css 와 충돌 방지).
 *
 * 데이터 출처 (모두 기존 API, 화면별로 필요한 것만 호출)
 * - 사이클  : /dashboard/machine/{id}/cycle       — 사출·보압·계량 프로파일(최근 2샷), 사이클 타임 트렌드, 사이클 통계
 * - 온도    : /dashboard/machine/{id}/temperature — Zone별 현재/설정/편차, 온도 트렌드(zoneN + setTempN), 편차 현황, 평균/최대편차
 * - 구동    : /dashboard/machine/{id}/drive       — 사출/계량/보압 구동부 현재값·%·작동시간·상태, 압력 트렌드, 모터 상태, 배압 모니터링
 *             /dashboard/machine/{id}/overview    — 금형(형개/형폐 위치·시간)·이젝터(전진/후퇴 위치) 설정값
 * - 감시    : /machine/watch1/{id}?sampleSize=N   — 현재 샷 실제값(Watch1Data) + 자동샘플링용 최근 N샷(현재 샷 제외)
 *             참고값·상/하한은 백엔드에 저장이 없어 최근 N샷 평균 ± kσ 로 화면에서 계산한다(추정 표기). 사용자설정 참고값은 저장 API 가 없어 비활성.
 * - 설정값  : /dashboard/machine/{id}/overview    — injection / holding / metering / mold / ejector 의 설정 항목 28개(최신 샷 기준)
 *             /machine/setting-changes/{id}?limit=N — "최근 설정 변경 이력" (기본 20건, "변경 이력 보기" 로 100건)
 *             OPCUA 는 원본에 '마지막 변경 1건'만 남아 샷 사이 여러 번 바꾸면 마지막 것만 보인다. 변경자는 우진 CMS 만 있다.
 *             "대시보드 위젯으로 추가" 는 Machine.jsx 의 대시보드 위젯(기본사출정보) 추가로 연결한다(onAddWidget).
 *
 * 백엔드에 없는 값은 만들지 않는다:
 * - 냉각 시간은 수집 항목이 아니라 사이클 − (사출+보압+계량) 으로 계산하며 범례에 "추정"을 붙인다.
 * - 프로파일 차트의 "설정 값" 기준선은 설정 데이터가 없어 그리지 않는다. 사이클 타임 트렌드에는 평균(실측)을 기준선으로 쓴다.
 * - 온도 통계의 최대 온도·표준편차는 Zone 현재 온도 5개로 화면에서 계산한다(집계 API 없음).
 * - 값이 null 이면 '—' 로 표시한다. */

const isNum = (v) => v != null && !Number.isNaN(Number(v));
const num = (v, digits = 1) => (isNum(v) ? Number(v).toFixed(digits) : '—');
const signed = (v, digits = 1) => {
  if (!isNum(v)) return '—';
  const n = Number(v);
  return `${n > 0 ? '+' : n < 0 ? '-' : ''}${Math.abs(n).toFixed(digits)}`;
};
const pct = (v) => (isNum(v) ? Math.max(0, Math.min(100, Number(v))) : 0);

const SUB_TABS = ['cycle', 'temperature', 'drive', 'monitor', 'setup'];

/* ---- 설정값 ----
 * 카드별 항목. section 은 overview 응답의 객체 이름, field 는 그 안의 필드, labelKey 는 기존 개요 탭 번역(pages.monitoring.machine.overview.labels) 재사용.
 * 단위는 MachineOverviewResponse 주석 기준. 보압전환모드는 문자열이라 단위 없음. */
const SETUP_CARDS = [
  { section: 'injection', titleKey: 'injection', items: [
    { field: 'maxPressure', labelKey: 'maxPressure', unit: 'MPa' },
    { field: 'startPosition', labelKey: 'startPosition', unit: 'mm' },
    { field: 'switchPosition', labelKey: 'switchPosition', unit: 'mm' },
    { field: 'time', labelKey: 'injectionTime', unit: 's' },
    { field: 'speed', labelKey: 'injectionSpeed', unit: 'mm/s' },
    { field: 'suckbackTime', labelKey: 'suckbackTime', unit: 's' },
    { field: 'suckbackSpeed', labelKey: 'suckbackSpeed', unit: 'mm/s' }
  ] },
  { section: 'holding', titleKey: 'holding', items: [
    { field: 'switchMode', labelKey: 'switchMode', unit: '', text: true },
    { field: 'switchInput', labelKey: 'switchInput', unit: 'mm' },
    { field: 'pressure1', labelKey: 'pressure1', unit: 'MPa' },
    { field: 'pressure2', labelKey: 'pressure2', unit: 'MPa' },
    { field: 'time1', labelKey: 'time1', unit: 's' },
    { field: 'time2', labelKey: 'time2', unit: 's' }
  ] },
  { section: 'metering', titleKey: 'metering', items: [
    { field: 'position', labelKey: 'meteringPosition', unit: 'mm' },
    { field: 'speed', labelKey: 'meteringSpeed', unit: 'rpm' },
    { field: 'pressure', labelKey: 'meteringPressure', unit: 'MPa' },
    { field: 'time', labelKey: 'meteringTime', unit: 's' }
  ] },
  { section: 'mold', titleKey: 'mold', items: [
    { field: 'thickness', labelKey: 'moldThickness', unit: 'mm' },
    { field: 'openPosition', labelKey: 'openPosition', unit: 'mm' },
    { field: 'closePosition', labelKey: 'closePosition', unit: 'mm' },
    { field: 'openTime', labelKey: 'openTime', unit: 's' },
    { field: 'closeTime', labelKey: 'closeTime', unit: 's' },
    { field: 'temp1', labelKey: 'moldTemp1', unit: '℃' },
    { field: 'temp2', labelKey: 'moldTemp2', unit: '℃' }
  ] },
  { section: 'ejector', titleKey: 'ejector', items: [
    { field: 'forwardPosition', labelKey: 'forwardPosition', unit: 'mm' },
    { field: 'backwardPosition', labelKey: 'backwardPosition', unit: 'mm' },
    { field: 'forwardSpeed', labelKey: 'forwardSpeed', unit: 'mm/s' },
    { field: 'backwardSpeed', labelKey: 'backwardSpeed', unit: 'mm/s' }
  ] }
];
/* 설정값은 소수 자릿수가 항목마다 달라(2.92, 283.9, 50) 불필요한 0 을 붙이지 않고 최대 2자리로 표시한다 */
const setupNum = (v) => (isNum(v) ? String(Number(Number(v).toFixed(2))) : '—');

/* ---- 감시 ----
 * 참고값 = 최근 WATCH_SAMPLE_SIZE 샷(현재 샷 제외) 평균, 상/하한 = 평균 ± WATCH_SIGMA_K × 표준편차 (자동샘플링, 추정) */
const WATCH_SAMPLE_SIZE = 20;
const WATCH_SIGMA_K = 3;
const WATCH_MIN_SAMPLES = 2; // 표준편차 계산에 필요한 최소 샷 수. 부족하면 참고값·상/하한 '—'
/* 감시표 열 정의 — field 는 /machine/watch1 응답(Watch1Response.Watch1Data) 필드, nameKey 는 기존 감시1 탭 번역(components.monitoringWatch1.cols) 재사용 */
const WATCH_TABLE1 = [
  { key: 'moldClose', field: 'moldCloseTime', nameKey: 'moldClose' },                 /* 형폐 (sec) */
  { key: 'moldProtect', field: 'moldProtectTime', nameKey: 'moldProtect' },           /* 금형보호 (sec) */
  { key: 'highPressure', field: 'highPressureTime', nameKey: 'highPressure' },        /* 고압 (sec) */
  { key: 'moldOpen', field: 'moldOpenTime', nameKey: 'moldOpen' },                    /* 형개 (sec) */
  { key: 'injectionComplete', field: 'injectionTime', nameKey: 'injectionComplete' }, /* 사출완료 (sec) */
  { key: 'cycleTime', field: 'cycleTime', nameKey: 'cycleTime' },                     /* 행정시간 (sec) */
  { key: 'extrusion', field: 'ejectTime', nameKey: 'extrusion' },                     /* 압출 (sec) */
  { key: 'moldOpenComplete', field: 'moldOpenPosition', nameKey: 'moldOpenComplete' } /* 형개완료 (mm) */
];
const WATCH_TABLE2 = [
  { key: 'maxInjectionPressure', field: 'injMaxPressure', nameKey: 'maxInjectionPressure' }, /* 사출최대압 (Bar) */
  { key: 'holdingPressure', field: 'holdPressure', nameKey: 'holdingPressure' },             /* 사출보압 */
  { key: 'turnPosition', field: 'turnPosition', nameKey: 'holdingTransfer', suffix: '(mm)' }, /* 보압전환 위치 */
  { key: 'turnTime', field: 'turnTime', nameKey: 'holdingTransfer', suffix: '(s)' },          /* 보압전환 시간 */
  { key: 'cushionAmount', field: 'cushionAmount', nameKey: 'cushionAmount' },                /* 쿠션양 (mm) */
  { key: 'injectionStart', field: 'injStartPosition', nameKey: 'injectionStart' },           /* 사출시작 (mm) */
  { key: 'metering', field: 'meteringPosition', nameKey: 'metering' },                       /* 계량 */
  { key: 'suckbackTime', field: 'suckbackTime', nameKey: 'suckbackTime' }                    /* 석백시간 (sec) */
];
/* 중요 감시 지정 기본값(디자인 시안의 체크 상태). 저장 API 가 없어 화면 상태로만 유지한다 */
const WATCH_DEFAULT_SELECTED = {
  table1: ['injectionComplete', 'cycleTime', 'moldOpenComplete'],
  table2: ['cushionAmount', 'injectionStart', 'metering']
};
const WATCH_ROWS = ['watchRowActual', 'watchRowDeviation', 'watchRowReference', 'watchRowUpper', 'watchRowLower', 'watchRowSelect'];

/* 열별 실제값·참고값·상/하한·편차 계산 (순수 함수) */
function computeWatchRows(cols, data) {
  return cols.map((col) => {
    const actual = data?.current?.[col.field];
    const samples = (data?.samples || []).map((s) => s?.[col.field]).filter(isNum).map(Number);
    let reference = null;
    let upper = null;
    let lower = null;
    if (samples.length >= WATCH_MIN_SAMPLES) {
      const mean = samples.reduce((a, b) => a + b, 0) / samples.length;
      const sd = Math.sqrt(samples.reduce((a, b) => a + (b - mean) * (b - mean), 0) / samples.length);
      reference = mean;
      upper = mean + WATCH_SIGMA_K * sd;
      lower = mean - WATCH_SIGMA_K * sd;
    }
    const deviation = isNum(actual) && isNum(reference) ? Number(actual) - reference : null;
    const out = isNum(actual) && isNum(upper) && isNum(lower) && (Number(actual) > upper || Number(actual) < lower);
    return { ...col, actual, deviation, reference, upper, lower, out };
  });
}

const COLORS = {
  blue: '#83A6FF', purple: '#A78BFA', amber: '#FDB022', pink: '#EC4899', green: '#47CD89',
  red: '#F04438', gray: '#98A8BF', white: '#FFFFFF'
};
const ZONE_COLORS = ['#83A6FF', '#A78BFA', '#FDB022', '#EC4899', '#47CD89'];
/* 온도 편차 현황 막대색 — 평균보다 높으면 빨강, 낮으면 초록 (디자인 지정색) */
const DEV_ABOVE_COLOR = '#F04438';
const DEV_BELOW_COLOR = '#47CD89';
const TEMP_TOLERANCE = 2; // ℃, 편차 허용 범위(디자인 표기). 기준이 정해지면 이 값만 바꾼다.

const TOOLTIP_STYLE = {
  contentStyle: { backgroundColor: '#1A1E24', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '4px', fontSize: '12px' },
  labelStyle: { color: '#98A8BF', fontSize: '12px' }
};
const AXIS_TICK = { fill: '#98A8BF', fontSize: 11 };
const AXIS_LINE = { stroke: 'rgba(255,255,255,0.12)' };

/* 범례 — recharts Legend 대신 패널 머리에 직접 그려 디자인(제목 오른쪽 색상 칩) 에 맞춘다 */
function LegendChips({ items }) {
  return (
    <div className="pr-legend">
      {items.map((it) => (
        <span key={it.label} className="pr-legend-item">
          <i className="pr-legend-swatch" style={{ background: it.color, borderStyle: it.dashed ? 'dashed' : 'solid' }} />
          {it.label}
        </span>
      ))}
    </div>
  );
}

function Panel({ title, right, className = '', children }) {
  return (
    <section className={`pr-panel ${className}`}>
      <div className="pr-panel-head">
        <h3 className="pr-panel-title">{title}</h3>
        {right}
      </div>
      {children}
    </section>
  );
}

function Empty({ text }) {
  return <div className="pr-empty">{text}</div>;
}

function StatusBadge({ tone, label }) {
  return <span className={`pr-badge pr-badge-${tone}`}>{label}</span>;
}

/* 선형 그라데이션 정의 — Area 채움용 */
function AreaDefs({ id, color }) {
  return (
    <defs>
      <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={color} stopOpacity={0.45} />
        <stop offset="100%" stopColor={color} stopOpacity={0.02} />
      </linearGradient>
    </defs>
  );
}

function ProcessNew({ equipmentId, initialSubTab = 'cycle', onAddWidget }) {
  const { t } = useTranslation();
  const tr = useCallback((key, params) => t(`pages.monitoring.machine.processNew.${key}`, params), [t]);

  const [subTab, setSubTab] = useState(SUB_TABS.includes(initialSubTab) ? initialSubTab : 'cycle');
  const [cycle, setCycle] = useState(null);
  const [temperature, setTemperature] = useState(null);
  const [drive, setDrive] = useState(null);
  const [overview, setOverview] = useState(null);
  const [watch, setWatch] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /* 사이클 탭 컨트롤 (기존 Cycle 탭과 같은 의미) */
  const [autoScale, setAutoScale] = useState(true);
  const [dataHold, setDataHold] = useState(false);
  const [chartFilter, setChartFilter] = useState('all');

  /* 감시 탭 컨트롤 */
  const [autoWatch, setAutoWatch] = useState(true);   // 미사용이면 상/하한 초과 표시를 끈다

  /* 설정값 탭: 설정 변경 이력 */
  const [settingChanges, setSettingChanges] = useState([]);
  const [historyAll, setHistoryAll] = useState(false);   // "변경 이력 보기" → 20건 ↔ 100건
  const [refMode, setRefMode] = useState('auto');     // 'auto' 만 동작. 'user' 는 저장 API 가 없어 비활성
  const [watchSelected, setWatchSelected] = useState(() => ({
    table1: Object.fromEntries(WATCH_TABLE1.map((c) => [c.key, WATCH_DEFAULT_SELECTED.table1.includes(c.key)])),
    table2: Object.fromEntries(WATCH_TABLE2.map((c) => [c.key, WATCH_DEFAULT_SELECTED.table2.includes(c.key)]))
  }));

  const load = useCallback(async (silent) => {
    if (!equipmentId) return;
    if (dataHold && silent) return;            // 데이터홀드: 자동 갱신만 멈춘다
    if (!silent) setIsLoading(true);
    setError(null);
    let tasks;
    if (subTab === 'cycle') tasks = [fetchMachineCycle(equipmentId)];
    else if (subTab === 'temperature') tasks = [fetchMachineTemperature(equipmentId)];
    else if (subTab === 'drive') tasks = [fetchMachineDrive(equipmentId), fetchMachineOverview(equipmentId)];
    else if (subTab === 'monitor') tasks = [fetchMachineWatch1(equipmentId, WATCH_SAMPLE_SIZE)];
    else tasks = [fetchMachineOverview(equipmentId), fetchMachineSettingChanges(equipmentId, historyAll ? 100 : 20)];   // setup

    const results = await Promise.allSettled(tasks);
    const values = results.map((r) => (r.status === 'fulfilled' ? r.value : null));
    results.forEach((r, i) => { if (r.status === 'rejected') console.error('Process(new) load failed:', subTab, i, r.reason); });

    if (subTab === 'cycle' && values[0]) setCycle(values[0]);
    if (subTab === 'temperature' && values[0]) setTemperature(values[0]);
    if (subTab === 'monitor' && values[0]) setWatch(values[0]);
    if (subTab === 'setup' && values[0]) setOverview(values[0]);
    if (subTab === 'setup' && values[1]) setSettingChanges(values[1].changes || []);
    if (subTab === 'drive') {
      if (values[0]) setDrive(values[0]);
      if (values[1]) setOverview(values[1]);
    }
    if (results[0].status === 'rejected') setError(tr('loadFailed'));
    if (!silent) setIsLoading(false);
  }, [equipmentId, subTab, dataHold, historyAll, tr]);

  useEffect(() => { load(false); }, [load]);
  useAutoRefresh(() => load(true), !!equipmentId);

  const selectSubTab = (key) => setSubTab(key);

  /* ---------------- 사이클 파생 ---------------- */
  const injShots = useMemo(() => cycle?.injectionProfile?.data || [], [cycle]);
  const holdShots = useMemo(() => cycle?.holdingProfile?.data || [], [cycle]);
  const meterShots = useMemo(() => cycle?.meteringProfile?.data || [], [cycle]);
  const stats = cycle?.cycleStatistics || null;

  /* 냉각(추정) = 사이클 − (사출+보압+계량). 음수면 null. */
  const trendData = useMemo(() => {
    const rows = cycle?.cycleTimeTrend?.data || [];
    return rows.map((r) => {
      const parts = [r.injectionTime, r.holdingTime, r.meteringTime];
      const cooling = isNum(r.cycleTime) && parts.every(isNum)
        ? Number(r.cycleTime) - parts.reduce((s, v) => s + Number(v), 0)
        : null;
      return { ...r, coolingEst: cooling != null && cooling >= 0 ? Number(cooling.toFixed(2)) : null };
    });
  }, [cycle]);

  /* 두 샷을 한 차트에 겹치려면 step 기준으로 행을 합친다 (recharts 는 한 data 배열이 편하다) */
  const mergeShots = useCallback((shots, keys) => {
    if (!shots.length) return [];
    const base = shots[0].profile || [];
    const second = shots[1]?.profile || [];
    return base.map((p, i) => {
      const row = { step: Number(p.step) };
      keys.forEach((k) => { row[k] = isNum(p[k]) ? Number(p[k]) : null; });
      keys.forEach((k) => { row[`${k}2`] = second[i] && isNum(second[i][k]) ? Number(second[i][k]) : null; });
      return row;
    });
  }, []);

  const injData = useMemo(() => mergeShots(injShots, ['speed', 'pressure']), [injShots, mergeShots]);
  const holdData = useMemo(() => mergeShots(holdShots, ['holding', 'time']), [holdShots, mergeShots]);
  const meterData = useMemo(() => mergeShots(meterShots, ['backPressure', 'cushion']), [meterShots, mergeShots]);
  const overlayLabel = (shots) => (shots.length >= 2
    ? tr('overlay', { a: shots[0].shotNumber, b: shots[1].shotNumber })
    : shots.length === 1 ? tr('singleShot', { a: shots[0].shotNumber }) : null);

  const yDomain = autoScale ? ['auto', 'auto'] : [0, 'auto'];
  const show = (key) => chartFilter === 'all' || chartFilter === key;

  /* ---------------- 온도 파생 ---------------- */
  const zones = useMemo(() => temperature?.zoneHeaters || [], [temperature]);
  const tempTrend = useMemo(() => temperature?.tempTrend?.data || [], [temperature]);
  const moldTemp = temperature?.moldTemperature || null;
  const zoneStats = useMemo(() => {
    const vals = zones.map((z) => z.currentTemp).filter(isNum).map(Number);
    if (!vals.length) return { mean: null, max: null, std: null };
    const mean = vals.reduce((s, v) => s + v, 0) / vals.length;
    const variance = vals.reduce((s, v) => s + (v - mean) ** 2, 0) / vals.length;
    return { mean, max: Math.max(...vals), std: Math.sqrt(variance) };
  }, [zones]);
  /* 온도 편차 현황: 전체 Zone 현재 온도의 평균을 가운데 기준선(0)으로 두고, 각 Zone 이 평균보다
   * 얼마나 높은지(+) / 낮은지(−) 를 막대로 그린다. 값은 zoneHeaters.currentTemp 로 화면에서 계산한다.
   * (백엔드 deviationStatus 는 '현재 − 설정' 이라 기준이 Zone 마다 달라 한눈에 비교가 안 된다는 피드백) */
  const avgDeviation = useMemo(() => {
    if (!isNum(zoneStats.mean)) return [];
    return zones
      .filter((z) => isNum(z.currentTemp))
      .map((z, i) => ({
        zoneName: z.zoneName || `Zone ${i + 1}`,
        currentTemp: Number(z.currentTemp),
        diff: Number((Number(z.currentTemp) - zoneStats.mean).toFixed(2))
      }));
  }, [zones, zoneStats.mean]);
  /* 0 이 정확히 가운데 오도록 위아래 대칭 범위. 최소 ±1℃ */
  const avgDevDomain = useMemo(() => {
    const m = Math.max(1, ...avgDeviation.map((d) => Math.abs(d.diff)));
    const bound = Math.ceil(m * 1.3 * 10) / 10;
    return [-bound, bound];
  }, [avgDeviation]);
  /* 트렌드 Y 범위: 현재/설정 값 전체에서 ±5℃ 여유 */
  const tempDomain = useMemo(() => {
    const vals = [];
    tempTrend.forEach((r) => { for (let i = 1; i <= 5; i += 1) { if (isNum(r[`zone${i}`])) vals.push(Number(r[`zone${i}`])); if (isNum(r[`setTemp${i}`])) vals.push(Number(r[`setTemp${i}`])); } });
    if (!vals.length) return ['auto', 'auto'];
    return [Math.floor(Math.min(...vals) - 5), Math.ceil(Math.max(...vals) + 5)];
  }, [tempTrend]);

  /* ---------------- 구동 파생 ---------------- */
  const driveUnits = drive ? [
    { key: 'injection', label: tr('driveInjection'), unit: drive.injectionDrive },
    { key: 'metering', label: tr('driveMetering'), unit: drive.meteringDrive },
    { key: 'holding', label: tr('driveHolding'), unit: drive.holdingDrive }
  ] : [];
  const driveTone = (status) => (status === '작동' ? 'ok' : status === '대기' ? 'idle' : 'stop');
  const driveLabel = (status) => (status === '작동' ? tr('statusRunning') : status === '대기' ? tr('statusIdle') : tr('statusStopped'));
  const pressureTrend = drive?.pressureTrend?.data || [];
  const motorData = useMemo(() => (drive?.motorStatus?.data || []).map((d) => ({ ...d, on: d.status === 2 ? 1 : 0 })), [drive]);
  const backPressure = drive?.backPressureTrend?.data || [];
  const moldSet = overview?.mold || null;
  const ejectorSet = overview?.ejector || null;

  /* ---------------- 감시 파생 ---------------- */
  const watchRows1 = useMemo(() => computeWatchRows(WATCH_TABLE1, watch), [watch]);
  const watchRows2 = useMemo(() => computeWatchRows(WATCH_TABLE2, watch), [watch]);
  const toggleWatchSelected = (tableId, key) => setWatchSelected((prev) => ({
    ...prev,
    [tableId]: { ...prev[tableId], [key]: !prev[tableId][key] }
  }));

  /* ---------------- 렌더 ---------------- */
  const renderSubTabs = () => (
    <div className="pr-subtab-row">
      <div className="pr-subtabs">
        {SUB_TABS.map((key) => (
          <button
            key={key}
            type="button"
            className={`pr-subtab ${subTab === key ? 'active' : ''}`}
            onClick={() => selectSubTab(key)}
          >
            {tr(`subTab.${key}`)}
          </button>
        ))}
      </div>
      {subTab === 'setup' && (
        <div className="pr-setup-actions">
          <button type="button" className={`pr-btn pr-btn-outline ${historyAll ? 'active' : ''}`} onClick={() => setHistoryAll((v) => !v)}>
            {historyAll ? tr('setupHistoryLess') : tr('setupHistoryButton')}
          </button>
          <button type="button" className="pr-btn pr-btn-primary" onClick={onAddWidget} disabled={!onAddWidget}>
            <span className="material-symbols-outlined" aria-hidden="true">add</span>
            {tr('setupAddWidget')}
          </button>
        </div>
      )}
      {subTab === 'cycle' && (
        <div className="pr-controls">
          <label className="pr-check">
            <input type="checkbox" checked={autoScale} onChange={(e) => setAutoScale(e.target.checked)} />
            {tr('autoScale')}
          </label>
          <label className="pr-check">
            <input type="checkbox" checked={dataHold} onChange={(e) => setDataHold(e.target.checked)} />
            {tr('dataHold')}
          </label>
          <span className="pr-control-label">{tr('chart')}</span>
          <select className="pr-select" value={chartFilter} onChange={(e) => setChartFilter(e.target.value)}>
            <option value="all">{tr('chartAll')}</option>
            <option value="injection">{tr('injectionProfile')}</option>
            <option value="trend">{tr('cycleTrend')}</option>
            <option value="holding">{tr('holdingProfile')}</option>
            <option value="metering">{tr('meteringProfile')}</option>
          </select>
        </div>
      )}
    </div>
  );

  const renderCycle = () => {
    if (!cycle) return <Empty text={tr('noData')} />;
    const single = chartFilter !== 'all';
    return (
      <>
        <div className={`pr-grid-2 ${single ? 'pr-grid-single' : ''}`}>
          {show('injection') && (
            <Panel
              title={tr('injectionProfile')}
              right={(
                <div className="pr-head-right">
                  <LegendChips items={[
                    { label: tr('legendSpeed'), color: COLORS.blue },
                    { label: tr('legendPressure'), color: COLORS.purple },
                    { label: tr('legendShot'), color: COLORS.amber }
                  ]} />
                  {overlayLabel(injShots) && <span className="pr-overlay">{overlayLabel(injShots)}</span>}
                </div>
              )}
            >
              <div className="pr-chart">
                {injData.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={injData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                      <AreaDefs id="pr-inj-speed" color={COLORS.blue} />
                      <AreaDefs id="pr-inj-pressure" color={COLORS.purple} />
                      <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="step" type="number" domain={[0, 1]} tick={AXIS_TICK} axisLine={AXIS_LINE} tickLine={false}
                        tickFormatter={(v) => `${Math.round(v * 100)}%`} />
                      <YAxis yAxisId="l" tick={AXIS_TICK} axisLine={false} tickLine={false} domain={yDomain} />
                      <YAxis yAxisId="r" orientation="right" hide domain={yDomain} />
                      <Tooltip {...TOOLTIP_STYLE} labelFormatter={(v) => `${tr('step')} ${Math.round(v * 100)}%`} />
                      <Area yAxisId="l" type="monotone" dataKey="pressure" name={`${tr('legendPressure')} (MPa)`} stroke={COLORS.purple} strokeWidth={1.5} fill="url(#pr-inj-pressure)" isAnimationActive={false} connectNulls />
                      <Area yAxisId="r" type="monotone" dataKey="speed" name={`${tr('legendSpeed')} (mm/s)`} stroke={COLORS.blue} strokeWidth={1.5} fill="url(#pr-inj-speed)" isAnimationActive={false} connectNulls />
                      {injShots[1] && (
                        <>
                          <Line yAxisId="l" type="monotone" dataKey="pressure2" name={`${tr('legendPressure')} #${injShots[1].shotNumber}`} stroke={COLORS.amber} strokeWidth={1} strokeDasharray="4 3" dot={false} isAnimationActive={false} connectNulls />
                          <Line yAxisId="r" type="monotone" dataKey="speed2" name={`${tr('legendSpeed')} #${injShots[1].shotNumber}`} stroke={COLORS.amber} strokeWidth={1} dot={false} isAnimationActive={false} connectNulls />
                        </>
                      )}
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : <Empty text={tr('noProfile')} />}
              </div>
            </Panel>
          )}

          {show('trend') && (
            <Panel
              title={tr('cycleTrend')}
              right={(
                <LegendChips items={[
                  { label: tr('legendMetering'), color: COLORS.blue },
                  { label: `${tr('legendCooling')}(${tr('estimated')})`, color: COLORS.purple, dashed: true },
                  { label: tr('legendHolding'), color: COLORS.amber },
                  { label: tr('legendInjection'), color: COLORS.pink },
                  { label: tr('legendCycle'), color: COLORS.green }
                ]} />
              )}
            >
              <div className="pr-chart">
                {trendData.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={trendData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                      <AreaDefs id="pr-trend-cycle" color={COLORS.green} />
                      <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="shotNumber" tick={AXIS_TICK} axisLine={AXIS_LINE} tickLine={false} />
                      <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} domain={yDomain} unit="s" />
                      <Tooltip {...TOOLTIP_STYLE} labelFormatter={(v) => `${tr('shotNo')} ${v}`} />
                      {isNum(stats?.averageCycle) && (
                        <ReferenceLine y={Number(stats.averageCycle)} stroke="#98A8BF" strokeDasharray="3 3"
                          label={{ value: `${tr('average')} ${num(stats.averageCycle, 2)}s`, position: 'insideTopRight', fill: '#C5D7E0', fontSize: 11 }} />
                      )}
                      <Area type="monotone" dataKey="cycleTime" name={`${tr('legendCycle')} (s)`} stroke={COLORS.green} strokeWidth={1.5} fill="url(#pr-trend-cycle)" isAnimationActive={false} connectNulls />
                      <Line type="monotone" dataKey="meteringTime" name={`${tr('legendMetering')} (s)`} stroke={COLORS.blue} strokeWidth={1.5} dot={false} isAnimationActive={false} connectNulls />
                      <Line type="monotone" dataKey="coolingEst" name={`${tr('legendCooling')} (${tr('estimated')}, s)`} stroke={COLORS.purple} strokeWidth={1.5} strokeDasharray="4 3" dot={false} isAnimationActive={false} connectNulls />
                      <Line type="monotone" dataKey="holdingTime" name={`${tr('legendHolding')} (s)`} stroke={COLORS.amber} strokeWidth={1.5} dot={false} isAnimationActive={false} connectNulls />
                      <Line type="monotone" dataKey="injectionTime" name={`${tr('legendInjection')} (s)`} stroke={COLORS.pink} strokeWidth={1.5} dot={false} isAnimationActive={false} connectNulls />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : <Empty text={tr('noTrend')} />}
              </div>
            </Panel>
          )}

          {show('holding') && (
            <Panel
              title={tr('holdingProfile')}
              right={(
                <div className="pr-head-right">
                  <LegendChips items={[
                    { label: tr('legendHoldingPressure'), color: COLORS.blue },
                    { label: tr('legendTime'), color: COLORS.purple }
                  ]} />
                  {overlayLabel(holdShots) && <span className="pr-overlay">{overlayLabel(holdShots)}</span>}
                </div>
              )}
            >
              <div className="pr-chart">
                {holdData.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={holdData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                      <AreaDefs id="pr-hold" color={COLORS.blue} />
                      <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="step" type="number" domain={[0, 1]} tick={AXIS_TICK} axisLine={AXIS_LINE} tickLine={false}
                        tickFormatter={(v) => `${Math.round(v * 100)}%`} />
                      <YAxis yAxisId="l" tick={AXIS_TICK} axisLine={false} tickLine={false} domain={yDomain} />
                      <YAxis yAxisId="r" orientation="right" hide domain={yDomain} />
                      <Tooltip {...TOOLTIP_STYLE} labelFormatter={(v) => `${tr('step')} ${Math.round(v * 100)}%`} />
                      <Area yAxisId="l" type="monotone" dataKey="holding" name={`${tr('legendHoldingPressure')} (MPa)`} stroke={COLORS.blue} strokeWidth={1.5} fill="url(#pr-hold)" isAnimationActive={false} connectNulls />
                      <Line yAxisId="r" type="monotone" dataKey="time" name={`${tr('legendTime')} (s)`} stroke={COLORS.purple} strokeWidth={1.5} dot={false} isAnimationActive={false} connectNulls />
                      {holdShots[1] && (
                        <Line yAxisId="l" type="monotone" dataKey="holding2" name={`${tr('legendHoldingPressure')} #${holdShots[1].shotNumber}`} stroke={COLORS.blue} strokeWidth={1} strokeDasharray="4 3" dot={false} isAnimationActive={false} connectNulls />
                      )}
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : <Empty text={tr('noProfile')} />}
              </div>
            </Panel>
          )}

          {show('metering') && (
            <Panel
              title={tr('meteringProfile')}
              right={(
                <div className="pr-head-right">
                  <LegendChips items={[
                    { label: tr('legendBackPressure'), color: COLORS.white },
                    { label: tr('legendCushion'), color: COLORS.gray }
                  ]} />
                  {overlayLabel(meterShots) && <span className="pr-overlay">{overlayLabel(meterShots)}</span>}
                </div>
              )}
            >
              <div className="pr-chart">
                {meterData.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={meterData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                      <AreaDefs id="pr-meter" color={COLORS.gray} />
                      <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="step" type="number" domain={[0, 1]} tick={AXIS_TICK} axisLine={AXIS_LINE} tickLine={false}
                        tickFormatter={(v) => `${Math.round(v * 100)}%`} />
                      <YAxis yAxisId="l" tick={AXIS_TICK} axisLine={false} tickLine={false} domain={yDomain} />
                      <YAxis yAxisId="r" orientation="right" hide domain={yDomain} />
                      <Tooltip {...TOOLTIP_STYLE} labelFormatter={(v) => `${tr('step')} ${Math.round(v * 100)}%`} />
                      <Area yAxisId="r" type="monotone" dataKey="cushion" name={`${tr('legendCushion')} (mm)`} stroke={COLORS.gray} strokeWidth={1.5} fill="url(#pr-meter)" isAnimationActive={false} connectNulls />
                      <Line yAxisId="l" type="monotone" dataKey="backPressure" name={`${tr('legendBackPressure')} (MPa)`} stroke={COLORS.white} strokeWidth={1.5} dot={false} isAnimationActive={false} connectNulls />
                      {meterShots[1] && (
                        <Line yAxisId="l" type="monotone" dataKey="backPressure2" name={`${tr('legendBackPressure')} #${meterShots[1].shotNumber}`} stroke={COLORS.white} strokeWidth={1} strokeDasharray="4 3" dot={false} isAnimationActive={false} connectNulls />
                      )}
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : <Empty text={tr('noProfile')} />}
              </div>
            </Panel>
          )}
        </div>

        <Panel title={tr('cycleStats')} className="pr-stats-panel">
          <div className="pr-stats pr-stats-5">
            {[
              { label: tr('statAvg'), value: stats?.averageCycle },
              { label: tr('statMin'), value: stats?.minCycle },
              { label: tr('statMax'), value: stats?.maxCycle },
              { label: tr('statStd'), value: stats?.stdDeviation, noUnit: true },
              { label: tr('statLatest'), value: stats?.latestCycle }
            ].map((s) => (
              <div key={s.label} className="pr-stat">
                <span className="pr-stat-label">{s.label}</span>
                <span className="pr-stat-value">
                  {num(s.value, 2)}{!s.noUnit && isNum(s.value) && <small>s</small>}
                </span>
              </div>
            ))}
          </div>
        </Panel>
      </>
    );
  };

  const renderTemperature = () => {
    if (!temperature) return <Empty text={tr('noData')} />;
    return (
      <>
        <div className="pr-zone-cards">
          {zones.length ? zones.map((z, i) => {
            const warn = z.status && z.status !== 'normal';
            return (
              <div key={z.zoneName || i} className={`pr-zone-card ${warn ? 'pr-zone-warn' : ''}`}>
                <div className="pr-zone-head">
                  <span className="pr-zone-name">{z.zoneName || `Zone ${i + 1}`}</span>
                  <StatusBadge tone={warn ? 'err' : 'ok'} label={warn ? tr('statusError') : tr('statusNormal')} />
                </div>
                <div className="pr-zone-temp">{num(z.currentTemp)}<small>℃</small></div>
                <div className="pr-zone-sub">
                  <span>{tr('setTemp')} <b>{num(z.setTemp)}</b><small>℃</small></span>
                  <span>{tr('deviation')} <b>{signed(z.deviation)}</b><small>℃</small></span>
                </div>
              </div>
            );
          }) : <Empty text={tr('noData')} />}
        </div>

        <div className="pr-grid-temp">
          <Panel
            title={tr('tempTrend')}
            right={<LegendChips items={ZONE_COLORS.map((c, i) => ({ label: zones[i]?.zoneName || `Zone ${i + 1}`, color: c }))} />}
          >
            <div className="pr-chart pr-chart-tall">
              {tempTrend.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={tempTrend} margin={{ top: 8, right: 8, left: -14, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="shotNumber" tick={AXIS_TICK} axisLine={AXIS_LINE} tickLine={false} />
                    <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} domain={tempDomain} unit="℃" />
                    <Tooltip {...TOOLTIP_STYLE} labelFormatter={(v) => `${tr('shotNo')} ${v}`} />
                    {ZONE_COLORS.map((c, i) => (
                      <React.Fragment key={i}>
                        <Line type="monotone" dataKey={`setTemp${i + 1}`} name={`${zones[i]?.zoneName || `Zone ${i + 1}`} ${tr('setTemp')}`} stroke={c} strokeOpacity={0.55} strokeWidth={1} strokeDasharray="2 3" dot={false} isAnimationActive={false} connectNulls />
                        <Line type="monotone" dataKey={`zone${i + 1}`} name={zones[i]?.zoneName || `Zone ${i + 1}`} stroke={c} strokeWidth={1.5} dot={false} isAnimationActive={false} connectNulls />
                      </React.Fragment>
                    ))}
                  </ComposedChart>
                </ResponsiveContainer>
              ) : <Empty text={tr('noTrend')} />}
            </div>
          </Panel>

          <Panel title={tr('deviationStatus')} right={<span className="pr-head-note">{tr('deviationFromAvg', { n: TEMP_TOLERANCE })}</span>}>
            <div className="pr-chart pr-chart-tall">
              {avgDeviation.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={avgDeviation} margin={{ top: 24, right: 8, left: 8, bottom: 0 }} barCategoryGap="30%">
                    <XAxis dataKey="zoneName" tick={AXIS_TICK} axisLine={AXIS_LINE} tickLine={false} />
                    <YAxis domain={avgDevDomain} tick={AXIS_TICK} axisLine={false} tickLine={false} tickFormatter={(v) => signed(v)} unit="℃" width={56} />
                    <Tooltip
                      {...TOOLTIP_STYLE}
                      formatter={(v, name, item) => [`${signed(v)} ℃ (${num(item?.payload?.currentTemp)} ℃)`, tr('diffFromAvg')]}
                    />
                    {/* 가운데 기준선 = 전체 Zone 평균. 0 위는 평균보다 높음(+), 아래는 낮음(−) */}
                    <ReferenceLine
                      y={0}
                      stroke="#98A8BF"
                      strokeDasharray="4 3"
                      label={{ value: tr('avgLine', { v: num(zoneStats.mean) }), position: 'insideTopLeft', fill: '#FFFFFF', fontSize: 12 }}
                    />
                    <ReferenceLine y={TEMP_TOLERANCE} stroke="#4B5563" strokeDasharray="2 4" />
                    <ReferenceLine y={-TEMP_TOLERANCE} stroke="#4B5563" strokeDasharray="2 4" />
                    <Bar dataKey="diff" isAnimationActive={false} radius={[2, 2, 2, 2]}>
                      {avgDeviation.map((d, i) => (
                        <Cell
                          key={i}
                          fill={d.diff > 0 ? DEV_ABOVE_COLOR : d.diff < 0 ? DEV_BELOW_COLOR : COLORS.gray}
                          fillOpacity={Math.abs(d.diff) > TEMP_TOLERANCE ? 1 : 0.75}
                        />
                      ))}
                      <LabelList dataKey="diff" position="top" formatter={(v) => signed(v)} fill="#FFFFFF" fontSize={12} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : <Empty text={tr('noData')} />}
            </div>
          </Panel>
        </div>

        <Panel title={tr('tempStats')} className="pr-stats-panel">
          <div className="pr-stats pr-stats-4">
            <div className="pr-stat">
              <span className="pr-stat-label">{tr('avgTemp')}</span>
              <span className="pr-stat-value">{num(moldTemp?.averageTemp)}{isNum(moldTemp?.averageTemp) && <small>℃</small>}</span>
            </div>
            <div className="pr-stat">
              <span className="pr-stat-label">{tr('maxDeviation')}</span>
              <span className="pr-stat-value">{signed(moldTemp?.maxDeviation)}{isNum(moldTemp?.maxDeviation) && <small>℃</small>}</span>
            </div>
            <div className="pr-stat">
              <span className="pr-stat-label">{tr('maxTemp')}</span>
              <span className="pr-stat-value">{num(zoneStats.max)}{isNum(zoneStats.max) && <small>℃</small>}</span>
            </div>
            <div className="pr-stat">
              <span className="pr-stat-label">{tr('statStd')}</span>
              <span className="pr-stat-value">{num(zoneStats.std, 2)}</span>
            </div>
          </div>
        </Panel>
      </>
    );
  };

  const renderDrive = () => {
    if (!drive) return <Empty text={tr('noData')} />;
    return (
      <>
        <div className="pr-grid-3">
          {driveUnits.map(({ key, label, unit }) => {
            const u = unit || {};
            const tone = driveTone(u.status);
            return (
              <section key={key} className={`pr-panel pr-drive-card pr-drive-${key} ${tone === 'ok' ? '' : 'pr-drive-inactive'}`}>
                <div className="pr-panel-head">
                  <h3 className="pr-panel-title">{label}</h3>
                  <StatusBadge tone={tone === 'ok' ? 'ok' : 'idle'} label={driveLabel(u.status)} />
                </div>
                {[
                  { label: tr('pressure'), value: u.pressure, unit: 'MPa', percent: u.pressurePercent },
                  { label: tr('speed'), value: u.speed, unit: 'mm/s', percent: u.speedPercent },
                  { label: tr('position'), value: u.position, unit: 'mm', percent: u.positionPercent }
                ].map((m) => (
                  <div key={m.label} className="pr-metric">
                    <div className="pr-metric-row">
                      <span className="pr-metric-label">{m.label}</span>
                      <span className="pr-metric-value">{num(m.value)}<small>{m.unit}</small></span>
                    </div>
                    <div className="pr-metric-bar-row">
                      <div className="pr-bar"><div className="pr-bar-fill" style={{ width: `${pct(m.percent)}%` }} /></div>
                      <span className="pr-metric-pct">{isNum(m.percent) ? `${Math.round(m.percent)}%` : '—'}</span>
                    </div>
                  </div>
                ))}
                <div className="pr-metric-row pr-metric-elapsed">
                  <span className="pr-metric-label pr-metric-label-sm">{tr('elapsed')}</span>
                  <span className="pr-metric-value">{num(u.elapsedTime, 2)}<small>sec</small></span>
                </div>
              </section>
            );
          })}
        </div>

        <div className="pr-grid-3">
          <Panel
            title={tr('pressureTrend')}
            right={<LegendChips items={[
              { label: tr('legendMeteringPressure'), color: COLORS.blue },
              { label: tr('legendBackPressure'), color: COLORS.purple },
              { label: tr('legendHoldingPressure'), color: COLORS.amber },
              { label: tr('legendInjectionPressure'), color: COLORS.pink }
            ]} />}
          >
            <div className="pr-chart">
              {pressureTrend.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={pressureTrend} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="shotNumber" tick={AXIS_TICK} axisLine={AXIS_LINE} tickLine={false} />
                    <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} />
                    <Tooltip {...TOOLTIP_STYLE} labelFormatter={(v) => `${tr('shotNo')} ${v}`} />
                    <Line type="monotone" dataKey="metering" name={`${tr('legendMeteringPressure')} (MPa)`} stroke={COLORS.blue} strokeWidth={1.5} dot={false} isAnimationActive={false} connectNulls />
                    <Line type="monotone" dataKey="backPressure" name={`${tr('legendBackPressure')} (MPa)`} stroke={COLORS.purple} strokeWidth={1.5} dot={false} isAnimationActive={false} connectNulls />
                    <Line type="monotone" dataKey="holding" name={`${tr('legendHoldingPressure')} (MPa)`} stroke={COLORS.amber} strokeWidth={1.5} dot={false} isAnimationActive={false} connectNulls />
                    <Line type="monotone" dataKey="injection" name={`${tr('legendInjectionPressure')} (MPa)`} stroke={COLORS.pink} strokeWidth={1.5} dot={false} isAnimationActive={false} connectNulls />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : <Empty text={tr('noTrend')} />}
            </div>
            <div className="pr-axis-note">{tr('axisPressureShot')}</div>
          </Panel>

          <Panel
            title={tr('motorStatus')}
            right={<LegendChips items={[{ label: 'ON', color: COLORS.white }, { label: 'OFF', color: COLORS.gray }]} />}
          >
            <div className="pr-chart">
              {motorData.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={motorData} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
                    <XAxis dataKey="shotNumber" tick={AXIS_TICK} axisLine={AXIS_LINE} tickLine={false} />
                    <YAxis domain={[0, 1]} ticks={[0, 1]} tickFormatter={(v) => (v === 1 ? 'ON' : 'OFF')} tick={AXIS_TICK} axisLine={false} tickLine={false} />
                    <Tooltip {...TOOLTIP_STYLE} labelFormatter={(v) => `${tr('shotNo')} ${v}`} formatter={(v) => [v === 1 ? 'ON' : 'OFF', tr('motorStatus')]} />
                    <Line type="stepAfter" dataKey="on" name={tr('motorStatus')} stroke={COLORS.green} strokeWidth={1.5} dot={false} isAnimationActive={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : <Empty text={tr('noTrend')} />}
            </div>
            <div className="pr-axis-note">{tr('axisStatusShot')}</div>
          </Panel>

          <Panel
            title={tr('backPressureMonitor')}
            right={<LegendChips items={[
              { label: tr('legendBackPressure'), color: COLORS.green },
              { label: tr('legendUpper'), color: COLORS.red, dashed: true },
              { label: tr('legendLower'), color: COLORS.blue, dashed: true }
            ]} />}
          >
            <div className="pr-chart">
              {backPressure.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={backPressure} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="shotNumber" tick={AXIS_TICK} axisLine={AXIS_LINE} tickLine={false} />
                    <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} />
                    <Tooltip {...TOOLTIP_STYLE} labelFormatter={(v) => `${tr('shotNo')} ${v}`} />
                    <Line type="monotone" dataKey="upperLimit" name={`${tr('legendUpper')} (MPa)`} stroke={COLORS.red} strokeWidth={1} strokeDasharray="3 3" dot={false} isAnimationActive={false} connectNulls />
                    <Line type="monotone" dataKey="lowerLimit" name={`${tr('legendLower')} (MPa)`} stroke={COLORS.blue} strokeWidth={1} strokeDasharray="3 3" dot={false} isAnimationActive={false} connectNulls />
                    <Line type="monotone" dataKey="backPressure" name={`${tr('legendBackPressure')} (MPa)`} stroke={COLORS.green} strokeWidth={1.5} dot={false} isAnimationActive={false} connectNulls />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : <Empty text={tr('noTrend')} />}
            </div>
            <div className="pr-axis-note">{tr('axisBackPressureShot')}</div>
          </Panel>
        </div>

        <Panel title={tr('moldEjectorSettings')} className="pr-stats-panel">
          {/* 출처: overview.mold / overview.ejector (최근 샷의 설정값). 없으면 '—' */}
          <table className="pr-table">
            <thead>
              <tr>
                <th>{tr('moldOpenPos')}</th>
                <th>{tr('moldClosePos')}</th>
                <th>{tr('moldOpenTime')}</th>
                <th>{tr('moldCloseTime')}</th>
                <th>{tr('ejectorForward')}</th>
                <th>{tr('ejectorBackward')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{num(moldSet?.openPosition)}{isNum(moldSet?.openPosition) && ' mm'}</td>
                <td>{num(moldSet?.closePosition)}{isNum(moldSet?.closePosition) && ' mm'}</td>
                <td>{num(moldSet?.openTime, 2)}{isNum(moldSet?.openTime) && ' s'}</td>
                <td>{num(moldSet?.closeTime, 2)}{isNum(moldSet?.closeTime) && ' s'}</td>
                <td>{num(ejectorSet?.forwardPosition)}{isNum(ejectorSet?.forwardPosition) && ' mm'}</td>
                <td>{num(ejectorSet?.backwardPosition)}{isNum(ejectorSet?.backwardPosition) && ' mm'}</td>
              </tr>
            </tbody>
          </table>
        </Panel>
      </>
    );
  };

  /* ---------------- 감시 ---------------- */
  const renderWatchTable = (titleKey, rows, tableId) => (
    <div className="pr-watch-block">
      <div className="pr-watch-title">{tr(titleKey)}</div>
      <div className="pr-watch-scroll">
        <div className="pr-watch-table">
          <div className="pr-watch-col pr-watch-col-label">
            <div className="pr-watch-cell pr-watch-head">{tr('watchRowGroup')}</div>
            {WATCH_ROWS.map((rowKey) => (
              <div key={rowKey} className="pr-watch-cell pr-watch-cell-label">{tr(rowKey)}</div>
            ))}
          </div>
          {rows.map((r) => {
            const selected = !!watchSelected[tableId][r.key];
            const out = autoWatch && r.out;
            return (
              <div key={r.key} className={`pr-watch-col ${selected ? 'pr-watch-col-important' : ''}`}>
                <div className="pr-watch-cell pr-watch-head">
                  {t(`components.monitoringWatch1.cols.${r.nameKey}`)}{r.suffix || ''}
                </div>
                <div className={`pr-watch-cell ${out ? 'pr-watch-out' : ''}`}>{num(r.actual, 2)}</div>
                <div className="pr-watch-cell">{signed(r.deviation, 2)}</div>
                <div className="pr-watch-cell">{num(r.reference, 2)}</div>
                <div className="pr-watch-cell">{num(r.upper, 2)}</div>
                <div className="pr-watch-cell">{num(r.lower, 2)}</div>
                <div className="pr-watch-cell">
                  <label className="pr-watch-check">
                    <input type="checkbox" checked={selected} onChange={() => toggleWatchSelected(tableId, r.key)} />
                    <span className="pr-watch-check-box" aria-hidden="true">
                      <span className="material-symbols-outlined">check</span>
                    </span>
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderMonitor = () => {
    const shotCount = watch?.current?.shotCount;        /* watch1.current.shotCount */
    const sampleCount = (watch?.samples || []).length;  /* watch1.samples — 자동샘플링에 실제로 쓰인 샷 수 */
    return (
      <section className="pr-panel pr-watch-panel">
        <div className="pr-watch-controls">
          <div className="pr-watch-controls-left">
            <label className="pr-watch-field">
              <span className="pr-watch-field-label">{tr('watchAutoWatch')}</span>
              <select className="pr-select pr-watch-select" value={autoWatch ? 'use' : 'notUse'} onChange={(e) => setAutoWatch(e.target.value === 'use')}>
                <option value="use">{tr('watchUse')}</option>
                <option value="notUse">{tr('watchNotUse')}</option>
              </select>
            </label>
            <label className="pr-watch-field">
              <span className="pr-watch-field-label">{tr('watchReference')}</span>
              <select className="pr-select pr-watch-select" value={refMode} onChange={(e) => setRefMode(e.target.value)}>
                <option value="auto">{tr('watchRefAuto')}</option>
                {/* 사용자설정 참고값은 저장 API 가 없어 선택 불가 */}
                <option value="user" disabled>{tr('watchRefUser')}</option>
              </select>
            </label>
            <div className="pr-watch-shot">
              <span className="pr-watch-field-label">shot</span>
              <b>{isNum(shotCount) ? shotCount : '—'}</b>
            </div>
            <span className="pr-watch-note">{tr('watchNote', { n: sampleCount, k: WATCH_SIGMA_K })}</span>
          </div>
          <div className="pr-watch-legend">
            <span className="pr-watch-legend-item pr-watch-legend-out"><i />{tr('watchLegendOut')}</span>
            <span className="pr-watch-legend-item pr-watch-legend-important"><i />{tr('watchLegendImportant')}</span>
          </div>
        </div>
        {watch?.current ? (
          <div className="pr-watch-blocks">
            {renderWatchTable('watchTable1', watchRows1, 'table1')}
            {renderWatchTable('watchTable2', watchRows2, 'table2')}
          </div>
        ) : (
          <Empty text={tr('noData')} />
        )}
      </section>
    );
  };

  /* ---------------- 설정값 ---------------- */
  const renderSetup = () => (
    <>
      <div className="pr-setup-cards">
        {SETUP_CARDS.map((card) => {
          const data = overview?.[card.section];   /* overview.injection / holding / metering / mold / ejector */
          return (
            <section key={card.section} className="pr-setup-card">
              <div className="pr-setup-card-head">
                <h3 className="pr-setup-card-title">{t(`pages.monitoring.machine.overview.${card.titleKey}`)}</h3>
                <span className="pr-setup-card-count">{tr('setupCount', { n: card.items.length })}</span>
              </div>
              <ul className="pr-setup-list">
                {card.items.map((item) => {
                  const raw = data?.[item.field];
                  const value = item.text ? (raw != null && raw !== '' ? String(raw) : '—') : setupNum(raw);
                  return (
                    <li key={item.field} className="pr-setup-row">
                      <span className="pr-setup-label">{t(`pages.monitoring.machine.overview.labels.${item.labelKey}`)}</span>
                      <span className="pr-setup-value">
                        {value}
                        {item.unit && <small>{item.unit}</small>}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>
      <Panel title={tr('setupHistoryTitle')}>
        <div className="pr-watch-scroll">
          <div className="pr-history-table">
            {['setupHistoryTime', 'setupHistoryItem', 'setupHistoryBefore', 'setupHistoryAfter', 'setupHistoryUser'].map((k) => (
              <div key={k} className="pr-watch-cell pr-watch-head">{tr(k)}</div>
            ))}
            {settingChanges.map((c, i) => {   /* /machine/setting-changes 응답 changes[] */
              const unit = c.unit ? ` ${c.unit}` : '';
              return (
                <React.Fragment key={`${c.changedAt}-${c.item}-${i}`}>
                  <div className="pr-watch-cell">{c.changedAt || '—'}</div>
                  <div className="pr-watch-cell" title={c.item}>{c.itemLabel || c.item}</div>
                  <div className="pr-watch-cell">{c.before != null && c.before !== '' ? `${c.before}${unit}` : '—'}</div>
                  <div className="pr-watch-cell">{c.after != null && c.after !== '' ? `${c.after}${unit}` : '—'}</div>
                  <div className="pr-watch-cell">{c.changedBy || '—'}</div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
        {settingChanges.length === 0 && <Empty text={tr('setupHistoryEmpty')} />}
      </Panel>
    </>
  );

  const currentData = subTab === 'cycle' ? cycle : subTab === 'temperature' ? temperature : subTab === 'monitor' ? watch : subTab === 'setup' ? overview : drive;

  return (
    <div className="pr-root">
      {renderSubTabs()}
      {isLoading && !currentData && (
        <div className="pr-message">{tr('loading')}</div>
      )}
      {error && <div className="pr-message pr-message-error">{error}</div>}
      {!isLoading || currentData ? (
        <>
          {subTab === 'cycle' && renderCycle()}
          {subTab === 'temperature' && renderTemperature()}
          {subTab === 'drive' && renderDrive()}
          {subTab === 'monitor' && renderMonitor()}
          {subTab === 'setup' && renderSetup()}
        </>
      ) : null}
    </div>
  );
}

export default ProcessNew;
